/**
 * Evidence chat adapter over the shared queue list.
 * Maps each queued message onto a row, and clears by removing items one by
 * one so the auto-send preference stays as the user set it.
 */

import { QueuedMessageList } from '@heidi/agents';
import { memo, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import type { QueuedMessage } from '../hooks/useChatQueue';

const queuedMessageAdapterMessages = defineMessages({
  attachmentCount: {
    defaultMessage: '{count, plural, one {# attachment} other {# attachments}}',
    id: 'kR4mN8pQx2',
    description: 'How many files are attached to a queued chat message',
  },
});

const attachmentCountFor = (message: QueuedMessage): number =>
  message.fileMetadata?.length || message.attachmentIds?.length || message.files?.length || 0;

interface QueuedMessagesProps {
  messages: QueuedMessage[];
  autoSendEnabled: boolean;
  onSendNow: (messageId: string) => void;
  onRemove: (messageId: string) => void;
  onEdit: (messageId: string, content: string) => void;
  onMoveUp: (messageId: string) => void;
  onToggleAutoSend: (enabled?: boolean) => void;
  className?: string;
}

export const QueuedMessages = memo(
  ({
    messages,
    autoSendEnabled,
    onSendNow,
    onRemove,
    onEdit,
    onMoveUp,
    onToggleAutoSend,
    className,
  }: QueuedMessagesProps) => {
    const { formatMessage } = useIntl();
    const items = useMemo(
      () =>
        messages.map((message) => {
          const attachmentCount = attachmentCountFor(message);
          return {
            id: message.id,
            text: message.content,
            editableText: message.content,
            secondaryText:
              attachmentCount > 0
                ? formatMessage(queuedMessageAdapterMessages.attachmentCount, {
                    count: attachmentCount,
                  })
                : undefined,
          };
        }),
      [formatMessage, messages]
    );

    return (
      <QueuedMessageList
        items={items}
        onSendNow={onSendNow}
        onRemove={onRemove}
        onClear={() => {
          for (const message of messages) {
            onRemove(message.id);
          }
        }}
        onEdit={onEdit}
        onMoveToFront={onMoveUp}
        autoSend={{
          enabled: autoSendEnabled,
          onToggle: () => onToggleAutoSend(),
        }}
        className={className}
      />
    );
  }
);

QueuedMessages.displayName = 'QueuedMessages';
export default QueuedMessages;
